import firebase from 'firebase/compat/app';
import 'firebase/compat/database';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
self.firebase = firebase;
import '../../../js/firebaseWorker/firebaseWorker.js';
import { PickAnAttribute } from '../../../modules/PickAnAttribute.js';
import { KatapultJob } from '../../../modules/katapult-job/KatapultJob.js';
import { GetMainPhoto } from '../../../modules/GetMainPhoto.js';
import { DataViews } from '../../../modules/DataViews.js';

/* global FirebaseWorker, google */

export async function insertCustomTension(
  multiSelectedConnections,
  jobId,
  jobCreator,
  nodes,
  traces,
  otherAttributes,
  modelDefaults,
  useMetricUnits
) {
  let update = {};
  const photos = await KatapultJob.fromId(jobId).getPhotos();
  const cableTypePicklists = otherAttributes?.cable_type?.picklists || {};
  let powerLookup = {};
  for (var i = 0; i < cableTypePicklists.power.length; i++) {
    powerLookup[cableTypePicklists.power[i].value] = true;
  }
  let commLookup = {};
  for (var i = 0; i < cableTypePicklists.communications.length; i++) {
    commLookup[cableTypePicklists.communications[i].value] = true;
  }
  const allWireSpecData =
    (await FirebaseWorker.ref(`photoheight/company_space/${jobCreator}/models/pole_loading/specs/wire_specs`)
      .once('value')
      .then((s) => s.val() || {})) || {};
  let nodeWarnings = [];
  let connectionWarnings = [];
  Object.entries(multiSelectedConnections).forEach(([connId, connection]) => {
    const fromNode = nodes[connection?.node_id_1] || {};
    const toNode = nodes[connection?.node_id_2] || {};
    const fromNodeOrderingAttribute = PickAnAttribute(fromNode.attributes, modelDefaults.ordering_attribute) || '';
    const toNodeOrderingAttribute = PickAnAttribute(toNode.attributes, modelDefaults.ordering_attribute) || '';
    const spanLength =
      google.maps.geometry.spherical.computeDistanceBetween(
        new google.maps.LatLng(fromNode.latitude, fromNode.longitude),
        new google.maps.LatLng(toNode.latitude, toNode.longitude)
      ) / 0.3048;
    const sections = connection?.sections || {};
    Object.values(sections).forEach((section) => {
      const mainPhotoId = GetMainPhoto(section.photos) || '';
      const mainPhotoData = photos[mainPhotoId]?.photofirst_data || {};
      const sectionMarkers = DataViews.help.getMarkers(mainPhotoData, traces, { allowProposed: true }) || [];
      sectionMarkers.forEach((marker) => {
        if (!marker.wire_tension || marker.wire_tension !== 'Slack') return;
        let potentialConnectionWarning = {
          key: connId,
          generalType: 'connection',
          description: `${fromNodeOrderingAttribute} to ${toNodeOrderingAttribute}`,
          warnings: []
        };
        const markerHeight = DataViews.help.getMarkerHeight(marker, useMetricUnits) || 'unknown height';
        if (!marker.wire_spec) {
          potentialConnectionWarning.warnings.push(`Marker at ${markerHeight} is missing wire spec.`);
          connectionWarnings.push(potentialConnectionWarning);
          return;
        }
        const cableType = traces[marker?._trace]?.cable_type || '';
        if (['ADSS', 'Power Guy'].includes(cableType)) {
          potentialConnectionWarning.warnings.push(`Marker at ${markerHeight} is ${cableType}, and was skipped.`);
          connectionWarnings.push(potentialConnectionWarning);
          return;
        }
        let loadCaseToUse = '';
        const fromNodeLoadCase = PickAnAttribute(fromNode.attributes, 'load_case');
        const fromNodeType = PickAnAttribute(fromNode.attributes, 'node_type');
        if (fromNodeType?.toLowerCase() == 'pole' && !fromNodeLoadCase) {
          if (nodeWarnings.find((warning) => warning.key === connection?.node_id_1)) return;
          nodeWarnings.push({
            key: connection?.node_id_1,
            generalType: 'node',
            description: fromNodeOrderingAttribute,
            warnings: [`Pole is missing load case attribute.`]
          });
          return;
        } else if (fromNodeType?.toLowerCase() == 'pole') loadCaseToUse = fromNodeLoadCase;
        if (!loadCaseToUse) {
          const toNodeLoadCase = PickAnAttribute(toNode.attributes, 'load_case');
          const toNodeType = PickAnAttribute(toNode.attributes, 'node_type');
          if (toNodeType?.toLowerCase() == 'pole' && !toNodeLoadCase) {
            if (nodeWarnings.find((warning) => warning.key === connection?.node_id_2)) return;
            nodeWarnings.push({
              key: connection?.node_id_2,
              generalType: 'node',
              description: toNodeOrderingAttribute,
              warnings: [`Pole is missing load case attribute.`]
            });
            return;
          }
          loadCaseToUse = toNodeLoadCase;
        }
        let loadCaseDistrict = '';
        const loadCaseDistrictOptions = ['Heavy', 'Medium', 'Light'];
        for (let i = 0; i < loadCaseDistrictOptions.length; i++) {
          if (loadCaseToUse.includes(loadCaseDistrictOptions[i])) loadCaseDistrict = loadCaseDistrictOptions[i].toLowerCase();
        }
        const wireSpecData = Object.values(allWireSpecData).find((table) => table.wire_spec === marker.wire_spec) || {};
        if (!wireSpecData.tension_tables?.full?.[loadCaseDistrict]) {
          potentialConnectionWarning.warnings.push(
            `Marker at ${markerHeight} has wire spec ${marker.wire_spec} with incorrectly configured full tension table.`
          );
          connectionWarnings.push(potentialConnectionWarning);
          return;
        }
        const tensionTable = wireSpecData.tension_tables.full[loadCaseDistrict];
        const tensionSpanLookup = Object.keys(tensionTable)
          .map((spanDistance) => Number(spanDistance))
          .sort((a, b) => a - b);
        const tensionSpanKey = String(tensionSpanLookup.find((spanDistance) => spanLength <= spanDistance)) || '';
        const fullTension = tensionTable[tensionSpanKey];
        let tensionPercentage = 0;
        if (['Street Light Feed', 'Power Drop'].includes(cableType)) tensionPercentage = 0.3;
        else if (powerLookup[cableType] || commLookup[cableType]) tensionPercentage = 0.5;
        if (tensionPercentage === 0) {
          potentialConnectionWarning.warnings.push(`Marker at ${markerHeight} is ${cableType}, and was therefore skipped.`);
          connectionWarnings.push(potentialConnectionWarning);
          return;
        }
        const customTension = fullTension * tensionPercentage;
        const markerIds = Object.keys(mainPhotoData.wire || {}) || [];
        const markerId = markerIds.find((key) => marker?._trace && mainPhotoData.wire?.[key]?._trace == marker._trace);
        update[`photos/${mainPhotoId}/photofirst_data/wire/${markerId}/custom_tension`] = customTension;
      });
    });
  });

  FirebaseWorker.ref(`photoheight/jobs/${jobId}`).update(update);
  nodeWarnings = nodeWarnings.sort((a, b) => a.description - b.description);
  if (nodeWarnings.length || connectionWarnings.length) return { title: 'Insert Custom Tension QC', nodeWarnings, connectionWarnings };
  return {};
}
